import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Drawer, CssBaseline, Toolbar, AppBar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import { alpha } from '@mui/material/styles';

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

const Layout = () => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isRtl, setIsRtl] = useState(i18n.language === 'dari' || i18n.language === 'ps');

  // Toggle drawer for mobile view
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // receive collapse toggle from Sidebar via custom event
  useEffect(() => {
    const handler = (e) => {
      setCollapsed(e.detail);
    };
    window.addEventListener('sidebar-toggle', handler);
    return () => window.removeEventListener('sidebar-toggle', handler);
  }, []);

  // receive mobile drawer toggle from Header via custom event
  useEffect(() => {
    const handler = () => {
      setMobileOpen(!mobileOpen);
    };
    window.addEventListener('mobile-drawer-toggle', handler);
    return () => window.removeEventListener('mobile-drawer-toggle', handler);
  }, [mobileOpen]);

  // Update RTL status when language changes
  useEffect(() => {
    setIsRtl(i18n.language === 'dari' || i18n.language === 'ps');
  }, [i18n.language]);

  const drawerWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', direction: isRtl ? 'rtl' : 'ltr' }}>
      <CssBaseline />
      
      {/* Sidebar / Navigation */}
      <Box
        component="nav"
        sx={{ 
          width: { sm: drawerWidth }, 
          flexShrink: { sm: 0 },
          zIndex: theme.zIndex.drawer,
          position: 'fixed',
          top: 0,
          [isRtl ? 'right' : 'left']: 0,
          height: '100vh',
        }}
      >
        {/* Mobile Drawer - shown when menu button clicked */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          anchor={isRtl ? 'right' : 'left'}
          ModalProps={{ keepMounted: true }} // Better performance on mobile
          sx={{
            display: { xs: 'block', sm: 'none' },
            zIndex: theme.zIndex.drawer + 2,
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              overflowX: 'hidden',
              [isRtl ? 'right' : 'left']: 0,
              [isRtl ? 'borderRight' : 'borderLeft']: 'none',
              top: 0,
              height: '100vh',
            },
          }}
        >
          <Sidebar collapsed={collapsed} />
        </Drawer>
        
        {/* Desktop Drawer - always visible */}
        <Drawer
          variant="permanent"
          anchor={isRtl ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            zIndex: theme.zIndex.drawer,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflowX: 'hidden',
              borderRight: isRtl ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderLeft: isRtl ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
              [isRtl ? 'right' : 'left']: 0,
              boxShadow: 'none',
              top: 0,
              height: '100vh',
              background: 'transparent',
            },
          }}
          open
        >
          <Sidebar collapsed={collapsed} />
        </Drawer>
      </Box>
      
      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          [isRtl ? 'marginRight' : 'marginLeft']: { sm: `${drawerWidth}px` },
        }}
      >
        {/* App Bar / Header */}
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            [isRtl ? 'right' : 'left']: { sm: `${drawerWidth}px` },
            zIndex: theme.zIndex.drawer + 1,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            top: 0,
            height: 64,
            minHeight: 64,
          }}
        >
          <Header />
        </AppBar>
        
        <Toolbar sx={{ height: 64, minHeight: 64 }} /> {/* Add spacing below app bar */}
        
        {/* Page content */}
        <Box sx={{ px: { xs: 4, sm: 4 }, py: { xs: 4, sm: 4 }, flexGrow: 1 }}>
          <Outlet />
        </Box>  
        
        {/* Footer */}
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout; 